import React, { Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import { Input } from '@fx-ui/fine-design';
import Intl from '@fx-ui/intl';
import { CaptchaDialog, I18nPhone, ValidCode } from '@fx-ui/platform-sdk';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import {
    compact,
    find,
    forEach,
    get,
    getApi,
    getErrorMsg,
    isEmpty,
    map,
    omit,
    post,
    repeat,
    size,
    trim,
    verifyEmail,
    verifyPhone
} from '~/utils';
import { IconFont, LoginName, LoginPassword } from '~/components';
import { API, ECode, ERROR_CODE, UGC_MAX_LENGTH } from '~/constants';

export enum AccountFieldType {
    phone = 'phone',
    phone_code = 'phone_code',
    email = 'email',
    email_code = 'email_code',
    /** 支持同时输入手机号和邮箱 */
    phone_email = 'phone_email',
    password = 'password',
    /** 无格式校验的密码框 */
    raw_password = 'raw_password',
    nickname = 'nickname'
}

export type AccountFormValue = Partial<Record<AccountFieldType, string>>;

export interface AccountFormRef {
    getValue: (skipValidate?: boolean) => AccountFormValue | undefined;
    // 显示指定字段的错误状态(仅标红)
    showError: (type: AccountFieldType) => void;
}

interface AccountField {
    type: AccountFieldType;
    disabled?: boolean;
    autoSelect?: boolean;
    needCache?: boolean;
    autoComplete?: boolean;
}

interface ValidationRule {
    required?: boolean;
    validate?: (val?: string) => boolean;
    message: string;
}

interface GetCodeParams {
    url: string;
    data: {
        email?: string;
        phone?: string;
    };
}

const { EMAIL, USER_NICKNAME } = UGC_MAX_LENGTH.USER;

const getValidationRules = (
    type: AccountFieldType,
    isPhoneMode?: boolean
): ValidationRule[] | undefined => {
    switch (type) {
        case AccountFieldType.phone: {
            return [
                { required: true, message: Intl.get('account.register.phone.empty') },
                {
                    validate: (value: string) => {
                        return verifyPhone(value, true);
                    },
                    message: Intl.get('phone.invalid.warning')
                }
            ];
        }
        case AccountFieldType.email: {
            return [
                { required: true, message: Intl.get('account.register.email.empty') },
                {
                    validate: (value: string) => {
                        return size(trim(value)) <= EMAIL;
                    },
                    message: Intl.get('max.length.error.message', { max: EMAIL })
                },
                {
                    validate: (value: string) => {
                        return verifyEmail(value);
                    },
                    message: Intl.get('email.invalid.warning')
                }
            ];
        }
        case AccountFieldType.phone_code:
        case AccountFieldType.email_code: {
            return [{ required: true, message: Intl.get('account.register.code.empty') }];
        }
        case AccountFieldType.password: {
            return [{ required: true, message: Intl.get('account.password.empty') }];
        }
        case AccountFieldType.nickname: {
            return [
                { required: true, message: Intl.get('account.nickname.enter') },
                {
                    validate: (value: string) => {
                        return size(trim(value)) <= USER_NICKNAME;
                    },
                    message: Intl.get('max.length.error.message', { max: USER_NICKNAME })
                }
            ];
        }
        case AccountFieldType.phone_email: {
            return compact([
                { required: true, message: Intl.get('account.signin.user.emptyTip') },
                isPhoneMode && {
                    validate: (value: string) => {
                        return verifyPhone(value, true);
                    },
                    message: Intl.get('phone.invalid.warning')
                }
            ]);
        }
        case AccountFieldType.raw_password: {
            return [{ required: true, message: Intl.get('account.signin.password.emptyTip') }];
        }
    }
};

const replaceNameWithAsterisk = (name = '') => {
    if (isEmpty(name)) {
        return '';
    }
    return get(name, 0) + repeat('*', size(name) - 1);
};

interface AccountFormProps {
    className?: string;
    defaultValue?: AccountFormValue;
    items: AccountField[];
    /**
     * 是否显示字段 errorMsg
     */
    showErrorMsg?: boolean;
    onChange?: () => void;
    onError?: (errorMsg: string) => void;
}

const AccountForm: React.ForwardRefRenderFunction<AccountFormRef, AccountFormProps> = (
    props,
    ref
) => {
    const { className, defaultValue, items, showErrorMsg = true, onChange, onError } = props;

    const [formValue, setFormValue] = useState(defaultValue);
    const [errorMap, setErrorMap] = useState<Partial<Record<AccountFieldType, string>>>();
    const [canGetCode, setCanGetCode] = useState(true);
    const [captchaDialogVisible, setCaptchaDialogVisible] = useState(false);
    const [nicknameChanged, setNicknameChanged] = useState(false);
    const [passwordFormatError, setPasswordFormatError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const codeRef = useRef<any>();

    useEffect(() => {
        if (!isEmpty(errorMap)) {
            setErrorMap(undefined);
        }
    }, [items]);

    useEffect(() => {
        setFormValue(defaultValue);
    }, [defaultValue]);

    useImperativeHandle(ref, () => {
        return {
            getValue: (skipValidate?: boolean) => {
                if (skipValidate) {
                    return formValue;
                }
                let hasError = false;
                forEach(items, ({ type }) => {
                    const { isValid, message = '' } = doValidate(type, formValue?.[type]);
                    if (isValid) {
                        // 密码格式校验比较特殊，在 LoginPassword 内完成，所以此处需要单独处理下
                        if (type === AccountFieldType.password && passwordFormatError) {
                            hasError = true;
                            addError(type, '');
                            onError?.(Intl.get('account.password.error'));
                            return false;
                        }
                        if (isError(type)) {
                            removeError(type);
                        }
                    } else {
                        hasError = true;
                        addError(type, '');
                        onError?.(message);
                        return false;
                    }
                });
                if (hasError) {
                    return;
                }
                return formValue;
            },
            showError: (type: AccountFieldType) => {
                addError(type, '');
            }
        };
    });

    const updateFormValue = (type: AccountFieldType, value?: string) => {
        setFormValue((prev) => {
            const newValue = {
                ...prev,
                [type]: value
            };
            return newValue;
        });
        onChange?.();
    };

    const isError = (type: AccountFieldType) => errorMap?.hasOwnProperty(type);

    const addError = (type: AccountFieldType, errorMsg: string) => {
        setErrorMap((prev) => {
            return {
                ...prev,
                [type]: errorMsg
            };
        });
    };

    const removeError = (type: AccountFieldType) => {
        setErrorMap((prev) => {
            return omit(prev, type);
        });
    };

    const doValidate = (type: AccountFieldType, value?: string, isPhoneMode?: boolean) => {
        const rules = getValidationRules(type, isPhoneMode);
        const failedRule = find(rules, ({ required, validate }) => {
            if (required) {
                return isEmpty(value);
            }
            if (validate) {
                return !validate(value);
            }
            return false;
        });
        return {
            isValid: !failedRule,
            message: failedRule?.message
        };
    };

    const handleValidate = (type: AccountFieldType, value?: string, isPhoneMode?: boolean) => {
        const { isValid, message = '' } = doValidate(type, value, isPhoneMode);
        if (isValid) {
            removeError(type);
        } else {
            addError(type, message);
        }
    };

    const handleGetCode = (params: GetCodeParams) => {
        // 加锁，避免重复点击
        setCanGetCode(false);
        return post(params.url, params.data, { customHandleHttpCodes: [400] })
            .then(() => {
                setCanGetCode(true);
                codeRef.current?.setReloadState();
            })
            .catch((error) => {
                setCanGetCode(true);
                const err = get(error, 'response.data', {});
                let errorMsg = getErrorMsg(err);
                switch (err.code) {
                    case ECode.USER_PHONE_FORMAT_INVALID:
                    case ECode.USER_PHONE_REQUIRED:
                        addError(AccountFieldType.phone, '');
                        break;
                    case ECode.MEMBER_PHONE_DUPLICATE:
                        addError(AccountFieldType.phone, '');
                        errorMsg = Intl.get('account.register.phone.exist');
                        break;
                    case ECode.USER_EMAIL_DOMAIN_BLOCKED:
                        addError(AccountFieldType.email, '');
                        break;
                    case ERROR_CODE.CAPTCHA:
                        setCaptchaDialogVisible(true);
                        errorMsg = '';
                        break;
                    default:
                        break;
                }
                if (errorMsg) {
                    onError?.(errorMsg);
                }
            });
    };

    const renderCaptchaDialog = (params: GetCodeParams) => {
        return (
            <CaptchaDialog
                visible={captchaDialogVisible}
                url={params.url}
                data={params.data}
                onClose={() => {
                    setCaptchaDialogVisible(false);
                }}
                onSuccess={() => {
                    setCaptchaDialogVisible(false);
                    codeRef.current?.setReloadState();
                }}
            />
        );
    };

    const renderValidCode = ({
        cls,
        type,
        value,
        isValid,
        canGetCode,
        params
    }: {
        cls: string;
        type: AccountFieldType;
        value?: string;
        isValid: boolean;
        canGetCode: boolean;
        params: GetCodeParams;
    }) => {
        return (
            <>
                <ValidCode
                    ref={codeRef}
                    className={cls}
                    height={40}
                    waterMark={Intl.get('account.register.code.placeholder')}
                    codeValid={isValid}
                    canGetCode={canGetCode}
                    onBtnClick={() => {
                        return handleGetCode(params);
                    }}
                    onChange={(val) => {
                        updateFormValue(type, val);
                    }}
                    onInputBlur={() => {
                        handleValidate(type, value);
                    }}
                />
                {renderCaptchaDialog(params)}
            </>
        );
    };

    const renderField = (field: AccountField) => {
        const { type, disabled, autoSelect, autoComplete, needCache } = field;
        const value = formValue?.[type];
        const isValid = !isError(type);
        const errMsg = showErrorMsg ? errorMap?.[type] : '';
        const cls = 'account-form-item';

        switch (type) {
            case AccountFieldType.phone: {
                return (
                    <I18nPhone
                        className={cls}
                        height={40}
                        waterMark={Intl.get('account.register.phone.placeholder')}
                        value={value}
                        disabled={disabled}
                        autoSelect={autoSelect}
                        needCache={needCache}
                        errorTrigger="blur"
                        errMsg={errMsg}
                        phoneValid={isValid}
                        onChange={(val) => {
                            updateFormValue(type, val);
                        }}
                        onBlur={(val) => {
                            handleValidate(type, val);
                        }}
                    />
                );
            }
            case AccountFieldType.email: {
                return (
                    <ErrorInput
                        className={cls}
                        placeholder={Intl.get('account.register.email.placeholder')}
                        value={value}
                        disabled={disabled}
                        autoSelect={autoSelect}
                        errorTrigger="blur"
                        inputState={isValid ? 'normal' : 'error'}
                        errMsg={errMsg}
                        onChange={(val) => {
                            updateFormValue(type, val);
                        }}
                        onBlur={(val) => {
                            handleValidate(type, val);
                        }}
                    />
                );
            }
            case AccountFieldType.phone_code: {
                const phoneValue = formValue?.[AccountFieldType.phone];
                const { isValid: isPhoneValid } = doValidate(AccountFieldType.phone, phoneValue);
                return renderValidCode({
                    cls,
                    type,
                    value,
                    isValid,
                    canGetCode: canGetCode && isPhoneValid,
                    params: {
                        url: getApi(API.authentication.send_sms),
                        data: { phone: phoneValue }
                    }
                });
            }
            case AccountFieldType.email_code: {
                const emailValue = formValue?.[AccountFieldType.email];
                const { isValid: isEmailValid } = doValidate(AccountFieldType.email, emailValue);
                return renderValidCode({
                    cls,
                    type,
                    value,
                    isValid,
                    canGetCode: canGetCode && isEmailValid,
                    params: {
                        url: getApi(API.authentication.send_email),
                        data: { email: emailValue }
                    }
                });
            }
            case AccountFieldType.password: {
                return (
                    <LoginPassword
                        className={cls}
                        waterMark={Intl.get('password')}
                        valid={false}
                        userInfo={{
                            email: formValue?.[AccountFieldType.email]
                        }}
                        onBlur={(value, isValid) => {
                            updateFormValue(type, value);
                            if (isValid) {
                                removeError(type);
                                setPasswordFormatError(false);
                            } else if (!isEmpty(value)) {
                                setPasswordFormatError(true);
                            }
                        }}
                    />
                );
            }
            case AccountFieldType.nickname: {
                return (
                    <ErrorInput
                        className={cls}
                        placeholder={Intl.get('your.name')}
                        value={nicknameChanged ? value : replaceNameWithAsterisk(value)}
                        errorTrigger="blur"
                        inputState={isValid ? 'normal' : 'error'}
                        errMsg={errMsg}
                        onFocus={() => {
                            if (!nicknameChanged) {
                                setNicknameChanged(true);
                                updateFormValue(type, '');
                            }
                        }}
                        onChange={(value) => {
                            updateFormValue(type, value);
                        }}
                        onBlur={(value) => {
                            handleValidate(type, value);
                        }}
                    />
                );
            }
            case AccountFieldType.phone_email: {
                return (
                    <LoginName
                        className={cls}
                        errorMsg={errMsg}
                        errorTrigger="blur"
                        value={value}
                        nameValid={isValid}
                        disabled={disabled}
                        needCache={needCache}
                        onChange={(value) => {
                            updateFormValue(type, value);
                        }}
                        onBlur={(value, isPhoneMode) => {
                            handleValidate(type, value, isPhoneMode);
                        }}
                    />
                );
            }
            case AccountFieldType.raw_password: {
                return (
                    <Input
                        className={cls}
                        type={showPassword ? 'text' : 'password'}
                        value={value}
                        placeholder={Intl.get('password')}
                        inputState={isValid ? 'normal' : 'error'}
                        autoComplete={autoComplete}
                        onChange={(value) => {
                            updateFormValue(type, value);
                        }}
                        suffix={
                            <IconFont
                                style={{ fontSize: 18 }}
                                type={showPassword ? 'eye-close' : 'eye-open'}
                                onClick={() => setShowPassword((v) => !v)}
                            />
                        }
                    />
                );
            }
        }
    };

    return (
        <div className={classNames('fx-account-form', className)}>
            {map(items, (item) => (
                <Fragment key={item.type}>{renderField(item)}</Fragment>
            ))}
        </div>
    );
};

export default React.forwardRef(AccountForm);
